
import { useMemo } from 'react';

interface Order {
  id: string;
  created_at: string;
  product_price: number;
  commission_amount: number;
  payment_status: string;
  product_category: string;
}

interface Props {
  orders: Order[];
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function RevenueChart({ orders }: Props) {
  const { dailyData, maxRevenue, categoryData } = useMemo(() => {
    const now = new Date();
    const last7: { label: string; date: string }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      last7.push({ label: DAYS[d.getDay()], date: d.toDateString() });
    }

    const completed = orders.filter(o => o.payment_status === 'completed');

    const dailyData = last7.map(({ label, date }) => {
      const dayOrders = completed.filter(
        o => new Date(o.created_at).toDateString() === date
      );
      return {
        label,
        revenue: dayOrders.reduce((s, o) => s + Number(o.product_price), 0),
        commission: dayOrders.reduce((s, o) => s + Number(o.commission_amount), 0),
        count: dayOrders.length,
      };
    });

    const maxRevenue = Math.max(...dailyData.map(d => d.revenue), 1);

    // Category breakdown
    const catMap: Record<string, number> = {};
    completed.forEach(o => {
      const cat = o.product_category || 'Other';
      catMap[cat] = (catMap[cat] || 0) + Number(o.product_price);
    });
    const total = Object.values(catMap).reduce((s, v) => s + v, 0) || 1;
    const COLORS = [
      'bg-teal-500',
      'bg-emerald-400',
      'bg-amber-400',
      'bg-rose-400',
      'bg-indigo-400',
      'bg-orange-400',
    ];
    const categoryData = Object.entries(catMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, value], i) => ({
        name,
        value,
        pct: Math.round((value / total) * 100),
        color: COLORS[i],
      }));

    return { dailyData, maxRevenue, categoryData };
  }, [orders]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
      {/* Bar Chart */}
      <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-semibold text-gray-900">Revenue — Last 7 Days</h3>
            <p className="text-xs text-gray-400 mt-0.5">Completed orders only</p>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-teal-500 inline-block"></span>
              Revenue
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-emerald-300 inline-block"></span>
              Commission
            </span>
          </div>
        </div>
        <div className="flex items-end gap-3 h-40">
          {dailyData.map((d, i) => (
            <div
              key={i}
              className="flex-1 flex flex-col items-center gap-1 group relative"
            >
              {/* Tooltip */}
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs rounded-lg px-2.5 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                <div className="font-semibold">
                  ${d.revenue.toFixed(2)}
                </div>
                <div className="text-gray-300">
                  Commission: ${d.commission.toFixed(2)}
                </div>
                <div className="text-gray-300">{d.count} orders</div>
              </div>
              <div
                className="w-full flex flex-col items-center gap-0.5 justify-end"
                style={{ height: '120px' }}
              >
                {/* Revenue bar */}
                <div
                  className="w-full bg-teal-500 rounded-t-md transition-all duration-500 hover:bg-teal-400 cursor-default"
                  style={{
                    height: `${Math.max(4, (d.revenue / maxRevenue) * 100)}px`,
                  }}
                ></div>
              </div>
              <span className="text-xs text-gray-400">{d.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h3 className="font-semibold text-gray-900 mb-1">Revenue by Category</h3>
        <p className="text-xs text-gray-400 mb-5">Completed orders</p>
        {categoryData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-300">
            <i className="ri-pie-chart-2-line text-3xl mb-2"></i>
            <span className="text-xs">No data yet</span>
          </div>
        ) : (
          <div className="space-y-3">
            {categoryData.map(cat => (
              <div key={cat.name}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-gray-700 font-medium truncate max-w-[120px]">
                    {cat.name}
                  </span>
                  <span className="text-gray-500">{cat.pct}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className={`${cat.color} h-2 rounded-full transition-all duration-700`}
                    style={{ width: `${cat.pct}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
